import { Star, Clock, ChevronLeft, ChevronRight } from "lucide-react";
import { useRef, useState } from "react";
import { useAITranslate, T } from "@/hooks/useAITranslate";

export interface SurfCourse {
  id: string;
  slug: string;
  title: string;
  category: string;
  description: string;
  duration: string;
  price: number;
  price_2h?: number;
  price_4h?: number;
  price_6h?: number;
  price_private?: number;
  price_semi_private?: number;
  price_group?: number;
  price_private_1h?: number;
  price_private_2h?: number;
  price_private_4h?: number;
  price_private_6h?: number;
  price_semi_private_1h?: number;
  price_semi_private_2h?: number;
  price_semi_private_4h?: number;
  price_semi_private_6h?: number;
  price_group_1h?: number;
  price_group_2h?: number;
  price_group_4h?: number;
  price_group_6h?: number;
  price_private_8h?: number;
  price_semi_private_8h?: number;
  price_group_8h?: number;
  price_private_10h?: number;
  price_semi_private_10h?: number;
  price_group_10h?: number;
  currency: string;
  rating: number;
  reviews_count: number;
  manual_reviews_count?: number;
  image_url: string | null;
  image_urls?: string[];
}

const ratingLabel = (r: number) => {
  if (r >= 4.8) return "Excellent";
  if (r >= 4.3) return "Très bien";
  if (r >= 3.5) return "Bien";
  return "Correct";
};

const SurfCourseCard = ({ course }: { course: SurfCourse }) => {
  const rating = Number(course.rating || 0);
  // Translate dynamic editorial fields via Lovable AI (cached in localStorage).
  // Reviews are intentionally NOT translated — see <reviews> section.
  const [tTitle, tDescription, tCategory] = useAITranslate([
    course.title,
    course.description,
    course.category,
  ]);
  const gallery = (course.image_urls && course.image_urls.length > 0)
    ? course.image_urls
    : (course.image_url ? [course.image_url] : []);
  const [imgIdx, setImgIdx] = useState(0);
  const currentImage = gallery[imgIdx] ?? null;
  const touchStartX = useRef<number | null>(null);

  const goPrev = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (gallery.length < 2) return;
    setImgIdx((i) => (i - 1 + gallery.length) % gallery.length);
  };
  const goNext = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (gallery.length < 2) return;
    setImgIdx((i) => (i + 1) % gallery.length);
  };
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) (dx < 0 ? goNext : goPrev)();
    touchStartX.current = null;
  };

  const tiers = ([
    {
      label: "Privé",
      sub: "1 élève",
      key: "private" as const,
      base: Number(course.price_private || 0),
      durations: {
        "1h": Number(course.price_private_1h || 0),
        "2h": Number(course.price_private_2h || 0),
        "4h": Number(course.price_private_4h || 0),
        "6h": Number(course.price_private_6h || 0),
        "8h": Number(course.price_private_8h || 0),
        "10h": Number(course.price_private_10h || 0),
      },
    },
    {
      label: "Semi-privé",
      sub: "2 élèves",
      key: "semi" as const,
      base: Number(course.price_semi_private || 0),
      durations: {
        "1h": Number(course.price_semi_private_1h || 0),
        "2h": Number(course.price_semi_private_2h || 0),
        "4h": Number(course.price_semi_private_4h || 0),
        "6h": Number(course.price_semi_private_6h || 0),
        "8h": Number(course.price_semi_private_8h || 0),
        "10h": Number(course.price_semi_private_10h || 0),
      },
    },
    {
      label: "Groupe",
      sub: "4 à 6",
      key: "group" as const,
      base: Number(course.price_group || 0),
      durations: {
        "1h": Number(course.price_group_1h || 0),
        "2h": Number(course.price_group_2h || 0),
        "4h": Number(course.price_group_4h || 0),
        "6h": Number(course.price_group_6h || 0),
        "8h": Number(course.price_group_8h || 0),
        "10h": Number(course.price_group_10h || 0),
      },
    },
  ]).filter((t) => t.base > 0 || Object.values(t.durations).some((p) => p > 0));

  const DURATIONS = ["1h", "2h", "4h", "6h", "8h", "10h"] as const;
  type Dur = (typeof DURATIONS)[number];

  const [tier, setTier] = useState<string>(tiers[0]?.key ?? "");
  const selectedTier = tiers.find((t) => t.key === tier);

  const firstAvailableDur = (t: typeof tiers[number] | undefined): Dur | "" => {
    if (!t) return "";
    const found = DURATIONS.find((d) => t.durations[d] > 0);
    return found ?? "";
  };
  const [duration, setDuration] = useState<Dur | "">(firstAvailableDur(selectedTier));

  // When tier changes, reset duration to first available for that tier
  const handleTierChange = (key: string) => {
    setTier(key);
    const t = tiers.find((x) => x.key === key);
    setDuration(firstAvailableDur(t));
  };

  const durationPrice =
    selectedTier && duration ? selectedTier.durations[duration] : 0;
  const displayPrice =
    durationPrice > 0
      ? durationPrice
      : selectedTier?.base ?? Number(course.price || 0);

  return (
    <article className="bg-white rounded-xl border border-gray-200 hover:border-primary/60 hover:shadow-md transition overflow-hidden flex flex-col">
      <div
        className="aspect-[4/3] bg-gradient-to-br from-primary to-accent relative group/img overflow-hidden"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {currentImage ? (
          <img
            src={currentImage}
            alt={`${course.title} — école de surf à Essaouira & Sidi Kaouki, Maroc`}
            loading="lazy"
            decoding="async"
            draggable={false}
            className="w-full h-full object-cover pointer-events-none select-none cursor-default transition-opacity duration-300"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-white/80 text-sm font-semibold">
            {tCategory}
          </div>
        )}
        <span className="absolute top-3 left-3 bg-white text-primary text-xs font-bold px-2 py-1 rounded-full shadow">
          {tCategory}
        </span>
        {gallery.length > 1 && (
          <>
            <span className="absolute top-3 right-3 bg-black/55 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full backdrop-blur-sm">
              {imgIdx + 1} / {gallery.length}
            </span>
            <button
              type="button"
              onClick={goPrev}
              aria-label="Image précédente"
              className="absolute left-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/85 hover:bg-white text-primary shadow-md flex items-center justify-center opacity-100 md:opacity-0 md:group-hover/img:opacity-100 transition-all duration-200 hover:scale-110"
            >
              <ChevronLeft className="w-5 h-5" strokeWidth={2.5} />
            </button>
            <button
              type="button"
              onClick={goNext}
              aria-label="Image suivante"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/85 hover:bg-white text-primary shadow-md flex items-center justify-center opacity-100 md:opacity-0 md:group-hover/img:opacity-100 transition-all duration-200 hover:scale-110"
            >
              <ChevronRight className="w-5 h-5" strokeWidth={2.5} />
            </button>
          </>
        )}
        {gallery.length > 1 && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1 px-2 py-1 rounded-full bg-black/30 backdrop-blur-sm">
            {gallery.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={(e) => { e.stopPropagation(); setImgIdx(i); }}
                aria-label={`Image ${i + 1}`}
                className={`h-[3px] rounded-full transition-all duration-300 ${i === imgIdx ? "w-5 bg-white" : "w-3 bg-white/60 hover:bg-white/90"}`}
              />
            ))}
          </div>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <h3 className="font-bold text-primary leading-snug">{tTitle}</h3>
        <div className="flex items-center gap-2 text-xs text-gray-600 mt-1.5">
          <span className="inline-flex items-center gap-0.5 text-yellow-500">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star
                key={i}
                className={`w-3.5 h-3.5 ${i < Math.round(rating) ? "fill-yellow-500" : "fill-none text-gray-500"}`}
              />
            ))}
          </span>
          <span className="font-bold text-primary">{rating.toFixed(1)}</span>
          <span className="text-primary font-semibold">{ratingLabel(rating)}</span>
          <span className="text-gray-600">·</span>
          <span>{(course.reviews_count || 0) + (course.manual_reviews_count || 0)} avis</span>
        </div>
        <p className="text-sm text-gray-700 mt-2 line-clamp-3">{tDescription}</p>
        {tiers.length > 0 && (
          <div className="mt-3">
            <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-1.5">
              <T>Choisissez votre formule</T>
            </p>
            <div className="grid grid-cols-3 gap-1.5">
              {tiers.map((t) => {
                const active = t.key === tier;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => handleTierChange(t.key)}
                    className={`rounded-md border px-2 py-1.5 text-xs font-semibold transition text-left ${
                      active
                        ? "border-primary bg-accent/15 text-primary"
                        : "border-gray-200 text-gray-700 hover:border-primary/50"
                    }`}
                  >
                    <div className="leading-tight"><T>{t.label}</T></div>
                    <div className="text-[10px] text-gray-500 leading-tight"><T>{t.sub}</T></div>
                    {t.base > 0 && (
                      <div className="text-[11px] font-bold text-primary mt-0.5">
                        <T>dès</T> {t.base} {course.currency}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            {selectedTier && DURATIONS.some((d) => selectedTier.durations[d] > 0) && (
              <div className="mt-3">
                <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-1.5">
                  <T>Choisissez la durée</T>
                </p>
                <div className="grid grid-cols-3 gap-1.5">
                  {DURATIONS.filter((d) => selectedTier.durations[d] > 0).map((d) => {
                    const price = selectedTier.durations[d];
                    const active = d === duration;
                    return (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setDuration(d)}
                        className={`rounded-md border px-2 py-1.5 text-xs font-semibold transition text-center ${
                          active
                            ? "border-primary bg-accent/15 text-primary"
                            : "border-gray-200 text-gray-700 hover:border-primary/50"
                        }`}
                      >
                        <div className="leading-tight">{d}</div>
                        <div className="text-[11px] font-bold text-primary mt-0.5">
                          {price} {course.currency}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
        <div className="mt-auto pt-4 flex items-end justify-between">
          <div>
            {course.duration && tiers.length === 0 && (
              <p className="text-xs text-gray-500 inline-flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" /> {course.duration}
              </p>
            )}
            {selectedTier && (
              <p className="text-xs text-gray-500 inline-flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" /> <T>Formule</T> <T>{selectedTier.label}</T>
                {duration ? ` · ${duration}` : ""}
              </p>
            )}
            <p className="text-xl font-extrabold text-primary leading-tight transition-all duration-200">
              {displayPrice > 0 ? `${displayPrice} ${course.currency}` : <T>Sur demande</T>}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              const payload = {
                formula: selectedTier?.label ?? "",
                duration: duration || "",
                course: course.title,
                price: displayPrice,
                currency: course.currency,
              };
              try {
                sessionStorage.setItem("surf_selected_formula", JSON.stringify(payload));
                window.dispatchEvent(new CustomEvent("surf:formula-selected"));
              } catch {}
              const priceLine =
                displayPrice > 0
                  ? `${displayPrice} ${course.currency}`
                  : "Prix sur demande";
              const lines = [
                "Bonjour Maroc Surfing, je souhaite réserver :",
                `- Cours : ${course.title}`,
                `- Formule : ${selectedTier?.label ?? "—"}${selectedTier?.sub ? ` (${selectedTier.sub})` : ""}`,
                `- Durée : ${duration || "—"}`,
                `- Prix : ${priceLine}`,
                "",
                "Source : page Maroc Surfing (carte cours)",
              ];
              const url = `https://wa.me/212706280215?text=${encodeURIComponent(lines.join("\n"))}`;
              window.open(url, "_blank", "noopener,noreferrer");
            }}
            className="inline-flex items-center justify-center bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-md px-3 py-2 transition-colors"
          >
            <T>Réserver</T>
          </button>
        </div>
      </div>
    </article>
  );
};

export default SurfCourseCard;